import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import messageService from '../services/messageService';
import { toast } from 'react-toastify';
import './Messages.css'; // CSS dosyasını aşağıda oluşturacağız

const Messages = () => {
  const { currentUser, userProfile } = useAuth();
  const location = useLocation();
  const scrollRef = useRef();

  const [allMessages, setAllMessages] = useState([]);
  const [conversations, setConversations] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // 1. Mesajları Dinle
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = messageService.subscribeToAllMessages(currentUser.uid, (messages) => {
      setAllMessages(messages);
      processConversations(messages);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // 2. TeacherProfile sayfasından "Mesaj Gönder" ile gelindiyse o kişiyi seç
  useEffect(() => {
    if (location.state?.startConversationWith) {
      const { id, name } = location.state.startConversationWith;
      // Eğer bu kişiyle daha önce konuşma yoksa, geçici olarak listeye ekle
      setConversations(prev => {
        if (!prev[id]) {
          return {
            ...prev,
            [id]: {
              userId: id,
              userName: name,
              messages: [],
              lastMessageTime: new Date().toISOString()
            }
          };
        }
        return prev;
      });
      setSelectedUserId(id);
    }
  }, [location.state]);

  // 3. Otomatik Scroll (Yeni mesaj geldiğinde en alta in)
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [allMessages, selectedUserId]);

  // Yardımcı: Ham mesajları kişilere göre grupla
  const processConversations = (messages) => {
    const groups = {};
    
    messages.forEach(msg => {
      // Mesajın diğer tarafı kim?
      const otherUserId = msg.senderId === currentUser.uid ? msg.receiverId : msg.senderId;
      const otherUserName = msg.senderId === currentUser.uid ? msg.receiverName : msg.senderName;

      if (!groups[otherUserId]) {
        groups[otherUserId] = {
          userId: otherUserId,
          userName: otherUserName || 'Unknown User',
          messages: [],
          lastMessageTime: null
        };
      }
      
      groups[otherUserId].messages.push(msg);
      // Son mesaj tarihi
      groups[otherUserId].lastMessageTime = msg.createdAt;
    });

    setConversations(groups);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUserId) return;

    try {
      const receiverName = conversations[selectedUserId].userName;
      const senderName = `${userProfile.firstName} ${userProfile.lastName}`;

      await messageService.sendMessage(
        currentUser.uid,
        selectedUserId,
        newMessage,
        senderName,
        receiverName
      );
      
      setNewMessage('');
    } catch (error) {
      console.error(error);
      toast.error('Message could not be sent');
    }
  };

  // Kişi listesini tarihe göre sırala (en yeni konuşma en üstte)
  const sortedContacts = Object.values(conversations).sort((a, b) => {
    return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
  });

  const activeConversation = selectedUserId ? conversations[selectedUserId] : null;

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  return (
    <div className="messages-page container">
      <div className="messages-container">
        {/* SOL PANEL: Kişi Listesi */}
        <div className="conversations-sidebar">
          <div className="sidebar-header">
            <h2>Messages</h2>
          </div>
          <div className="conversations-list">
            {sortedContacts.length === 0 ? (
              <p className="no-convos">No conversations yet.</p>
            ) : (
              sortedContacts.map(convo => (
                <div 
                  key={convo.userId} 
                  className={`conversation-item ${selectedUserId === convo.userId ? 'active' : ''}`}
                  onClick={() => setSelectedUserId(convo.userId)}
                >
                  <div className="convo-avatar">
                    {convo.userName.charAt(0).toUpperCase()}
                  </div>
                  <div className="convo-info">
                    <h4>{convo.userName}</h4>
                    <p>{convo.messages.length > 0 
                        ? convo.messages[convo.messages.length - 1].text.substring(0, 30) + '...' 
                        : 'Start a conversation'}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* SAĞ PANEL: Chat Penceresi */}
        <div className="chat-window">
          {activeConversation ? (
            <>
              <div className="chat-header">
                <div className="header-avatar">
                  {activeConversation.userName.charAt(0).toUpperCase()}
                </div>
                <h3>{activeConversation.userName}</h3>
              </div>
              
              <div className="messages-list">
                {activeConversation.messages.map((msg, index) => {
                  const isMe = msg.senderId === currentUser.uid;
                  return (
                    <div key={msg.id || index} className={`message-bubble-container ${isMe ? 'mine' : 'theirs'}`}>
                      <div className="message-bubble">
                        {msg.text}
                      </div>
                      <div className="message-time">
                        {new Date(msg.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </div>
                    </div>
                  );
                })}
                <div ref={scrollRef} />
              </div>

              <form className="message-input-area" onSubmit={handleSendMessage}>
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                />
                <button type="submit" disabled={!newMessage.trim()}>
                  Send ➤
                </button>
              </form>
            </>
          ) : (
            <div className="no-chat-selected">
              <div style={{fontSize: '50px'}}>💬</div>
              <h3>Select a conversation</h3>
              <p>Choose a contact from the left to start chatting</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;